import { useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { E2E_MOCK } from '../lib/e2eMock';
import { markShown, wasShown } from '../lib/notifyDedupe';
import { toastForOutboxKind } from '../lib/outboxToast';
import { showToast } from '../lib/toast';
import { useAuth } from '../lib/useAuth';
import { supabase } from '../lib/supabase';
import {
  planNotificationTick,
  type NotificationOutboxItem,
} from './notificationOutboxTick';


const POLL_INTERVAL_MS = 60_000;
/** 앱 복귀 후 토스트를 띄우기까지의 유예 — 복귀 전환 애니메이션과 겹치지 않게 한다. */
const RESUME_POLL_DELAY_MS = 400;

export function useNotificationOutbox(): void {
  const { user } = useAuth();
  const inFlight = useRef(false);

  const poll = useCallback(async () => {
    if (E2E_MOCK || !user || inFlight.current) return;
    inFlight.current = true;
    try {
      const { data, error } = await supabase
        .from('notification_outbox')
        .select('id,kind,ref_id,title,body,link')
        .in('status', ['sent', 'capped'])
        .is('read_at', null)
        .order('sent_at', { ascending: true })
        .limit(20);
      if (error) {
        console.warn('[notifications:poll] 미읽음 조회 실패', error.message);
        return;
      }

      const plan = planNotificationTick((data || []) as NotificationOutboxItem[], wasShown);
      if (plan.length === 0) return;

      for (const { row, shouldShow } of plan) {
        const refId = String(row.ref_id ?? '');
        if (!shouldShow) continue;
        markShown(row.kind, refId);
        const mapped = toastForOutboxKind(row.kind);
        showToast({
          kind: mapped.kind,
          expr: mapped.expr,
          title: row.title,
          subtitle: row.body,
          link: row.link || undefined,
        });
      }

      try {
        await apiFetch('/api/mobile/notifications/read', {
          method: 'POST',
          body: { ids: plan.map(({ row }) => row.id) },
        });
      } catch (error) {
        console.warn('[notifications:read] 읽음 처리 실패 — 다음 틱에 재시도합니다', error);
      }
    } finally {
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (E2E_MOCK || !user) return undefined;

    void poll();
    const timer = window.setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
    // 앱 복귀 즉시 토스트를 띄우면 화면 전환 애니메이션과 겹쳐 "잠깐 보였다 사라지는" 것처럼 읽힌다
    // (2026-07-25 실기 지적). 전환이 끝난 뒤 띄우도록 한 박자 늦춘다.
    let resumeTimer = 0;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => { void poll(); }, RESUME_POLL_DELAY_MS);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(resumeTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [poll, user]);
}
